import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationExtras, Route, Router } from '@angular/router';
import { ProjectRoutes } from '@app/cem/project/project.routing';
import { AuthConstants } from '@app/common/constants/AuthConstants';
import { PlexEnvironment } from '@app/env';
import { ForecastsRoutes } from '@app/forecasts/forecasts.routing';
import { ReportsRoutes } from '@app/reports/reports.routing';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, InteractionType, PopupRequest, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { LocalStorageService } from 'app/auth/local-storage.service';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthLocalStorageKeys } from './auth-local-storage-keys';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  public router = inject(Router);
  private localStorage = inject(LocalStorageService);
  private location = inject(Location);
  private msalService = inject(MsalService);
  public environment = inject(PlexEnvironment);

  private _redirectUrl: string[];
  private _redirectExtras?: NavigationExtras;
  private _raw: string = null;
  userProfile: any;
  public permissions: any;
  isIframe = false;
  loginDisplay = false;

  public $permissions = new Subject<void>();
  private readonly _destroying$ = new Subject<void>();

  public authorized = false;
  public authorizing = false;

  public childLookup: any[] = [];

  private AppRoutes: Array<Route> = new Array<Route>();

  constructor() { 
    this.populateRoutes();
  }

  private populateRoutes() {
    //mimic app-routing.module.ts, but without lazy loading
    //used for navigation permissions
    this.AppRoutes.push({
        path: "projects",
        children: ProjectRoutes
      } as Route);
    this.AppRoutes.push({
        path: "forecasts",
        children: ForecastsRoutes
      } as Route);
    this.AppRoutes.push({
        path: "reports",
        children: ReportsRoutes
      } as Route);
  }

  public getRedirect(): { url: string[], extras: NavigationExtras } {
    if (this._raw === null) return null;

    return { url: this._redirectUrl, extras: this._redirectExtras };
  }

  public get redirectUrl(): string { return this._raw };

  public set redirectUrl(value: string) {
    this._raw = value;
    if (this._raw === null) {
      this._redirectUrl = null;
      this._redirectExtras = null;
      return;
    }
    let parsed = this.ParseUrl(value);
    this._redirectUrl = parsed.url;
    this._redirectExtras = parsed.extras;

  }

  public ParseUrl(raw: string): { url: string[], extras: NavigationExtras } {
    let redirectUrlParts = raw.split('?');
    let queryParams = {};

    if (redirectUrlParts.length > 1) {
      let params = redirectUrlParts[1].split('&');

      params.forEach(param => {
        let [key, value] = param.split('=');
        queryParams[key] = value;
      });
    }
    let extras: NavigationExtras = { queryParams: queryParams };
    return { url: [redirectUrlParts[0]], extras };
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  public login(): void {
    //NOTE: the msalGuardConfig is setup in the app.module.ts, MSALGuardConfigFactory
    //      Go there to configure the scopes, and failure re-route url

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.handleLoginResponse(response);
          });
      } else {
        this.msalService.loginPopup()
          .subscribe((response: AuthenticationResult) => {
            this.handleLoginResponse(response);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest).subscribe(() => this.handleLoginResponse(null));
      } else {
        this.msalService.loginRedirect().subscribe(() => this.handleLoginResponse(null));
      }
    }
  }

  handleLoginResponse(response: AuthenticationResult) {
    if (response) {
      this.msalService.instance.setActiveAccount(response.account);
    }

    if (this.redirectUrl) {
      console.warn(`after login, re-routing to ${this.redirectUrl}`);
      let parts = this.getRedirect();
      this.router.navigate(parts.url, parts.extras);
      this.redirectUrl = null;
    }
  }

  private getAppRolesForUser(activeAccount : AccountInfo) : Array<string> {
    let cachedRoles = this.localStorage.getItemNow<Array<string>>(AuthLocalStorageKeys.Roles, []);
    if (cachedRoles && cachedRoles.length > 0)
    {
      return cachedRoles;
    }

    activeAccount = activeAccount ?? this.msalService.instance.getActiveAccount();
    if (activeAccount)
    {
      let roles = activeAccount.idTokenClaims.roles;
    
      if (roles && roles.length > 0)
      {
        let result = new Array<string>();
        for (let key in AuthConstants.ROLES) 
        {
          if (roles.includes(AuthConstants.ROLES[key].name))
          {
            result.push(AuthConstants.ROLES[key].name);
          }
        }
        
        if (cachedRoles != result)
        {
          this.localStorage.setItemNow(AuthLocalStorageKeys.Roles, roles);
        }

        return result;
      }
    }

    return null;
  }

  public async authorize(tries: number = 1): Promise<any> {
    if (this.authorizing && !this.authorized) {
      // await this.$gotScopes;
      return;
    }
    if (this.authorized) {
      this.$permissions.next();
      return;
    }

    const env = this.environment;
    if (env.security) {
      this.authorizing = true;

      try {
        let activeAccount = this.msalService.instance.getActiveAccount();

        if (activeAccount) {
          let username = activeAccount.username;
          sessionStorage.setItem("_userName_", username);

          this.getAppRolesForUser(activeAccount);

          this.authorized = true;

          await this.getToken(activeAccount);

          this.setSessionAndRedirect();
        }
      }
      catch (error) {
        console.error(`Error while authorizing...`, error);
        setTimeout(() => this.authorize(tries + 1), 3000);
      }
      finally {
        this.authorizing = false;
      }
    }
    else {
      sessionStorage.setItem("_userName_", this.environment.username);
    }
  }

  public async getToken(account: AccountInfo): Promise<string> {
    const env = this.environment;

    const scopes = env.apiConfig.accessScopes;
    if (scopes.length == 0 || (scopes.length == 1 && scopes[0] == ""))
      throw new Error('Missing accessScopes from apiConfig in environment settings.');

    const accessTokenRequest: SilentRequest =
    {
      scopes: scopes,
      account: account,
    };

    let token = '';
    try {
      const tokenResponse = await firstValueFrom(this.msalService.acquireTokenSilent(accessTokenRequest));
      token = tokenResponse.accessToken;
      if (token != env.accessToken) {
        env.accessTokenExpiresOn = tokenResponse.expiresOn;
        env.accessToken = tokenResponse.accessToken;
        if (!tokenResponse.fromCache) {
          env.accessToken = token;
        } else {
          this.clearRoles();
        }
      }
    }
    catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        const tokenResponse = await firstValueFrom(this.msalService.acquireTokenPopup(accessTokenRequest));
        token = tokenResponse.accessToken;
        if (!tokenResponse.fromCache) {
          env.accessToken = token;
        } else {
          this.clearRoles();
        }
        env.accessTokenExpiresOn = tokenResponse.expiresOn;
      }
    }
    return token;
  }

  private failed: boolean = false;

  private setSessionAndRedirect() {
    const expiration = this.environment.accessTokenExpiresOn;
    this.localStorage.setItemNow(AuthLocalStorageKeys.ExpireAt, expiration.getTime());

    this.$permissions.next();
    this.redirect();
  }

  public clearRoles() {
    this.localStorage.removeItem(AuthLocalStorageKeys.Roles);
  }

  public activeAccount(): string {
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      return account.name ?? account.username;
    } else {
      return '';
    }
  }

  public isUserLoggedIn(): boolean {
    if (this.msalService.instance.getActiveAccount() != null) {
      return true;
    }
    return false;
  }

  public async logout() {
    // Remove tokens and expiry time from localStorage
    await this.msalService.logoutPopup({
      mainWindowRedirectUri: '/'
    }).toPromise();
  }

  public setLoggedOut() {
    ///this will be called from the msal service upon logout
    this.localStorage.removeItem(AuthLocalStorageKeys.ExpireAt);
    this.clearRoles();
  }

  public userAuthenticatedAndRolesLoadedAndCurrent(): boolean {
    let account = this.msalService.instance.getActiveAccount();
    const authenticated = account != null;
    if (!authenticated) return false;

    const expiresAt = this.localStorage.getItemNow<number>(AuthLocalStorageKeys.ExpireAt);
    const current = new Date().getTime() < expiresAt;
    if (!current) return false;

    const cachedRoles = this.getAppRolesForUser(account);
    return cachedRoles?.length > 0;
  }

  public isAuthenticated(): boolean {

    const authenticated = this.msalService.instance.getActiveAccount() != null;
    if (!authenticated) return false;

    // Check whether the current time is past the access token's expiry time
    const expiresAt = this.localStorage.getItemNow<number>(AuthLocalStorageKeys.ExpireAt);
    return new Date().getTime() < expiresAt;
  }

  public failedAuthentication(): boolean {
    return this.failed;
  }

  public userHasRole(role : string) : boolean {
    const cachedRoles = this.getAppRolesForUser(null) ?? [];
    if (cachedRoles.includes(AuthConstants.ROLES.Developer.name))
      return true;
    else return cachedRoles.includes(role);
  }

  public userHasAnyRole(roles: Array<string> | string) : boolean {
    if (typeof roles === 'string')
      return this.userHasAnyRole([roles]);

      let result = false;
      roles.forEach(role => {
        if (this.userHasRole(role)){
          result = true;
        }
      });

      return result;
  }

  public redirect() {
    if (this.isAuthenticated() && this.environment.security) {
      let currentPath = this.location.path();
      if (currentPath === '' || currentPath == '/access-denied') {
        this.router.navigate(['projects', 'home']);
      } else {
        let routeTo = this.getRedirect();
        if (routeTo) {
          this.router.navigate(routeTo.url, routeTo.extras);
        }
      }
    } else if (this.environment.security === false) {

      this.router.navigate(['projects', 'home']);
    } else {
      console.warn('auth.service re-routing to access-denied');
      this.router.navigate(['access-denied']);
    }
  }
}